import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../utils/axios";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { CONFIG } from "../../../config";
import { useMediaQuery } from "@mui/material";
import { codeFormatter, valueDateFormatter } from "../../../utils/formatters";
import useLang from "../../../hooks/useLang";
import DefaultSelect from "../../../components/form/DefaultSelect";
import { Button } from "antd";
import { toast } from "react-toastify";
import { getConfigurationValue } from "../../../lib/OptionListApi";

type SelectIncorrectOrderDetails = {
	refId: string | null;
	buyerNameAndSurname?: string | null;
	buyerPhoneNo?: string | null;
	country?: string | null;
	city?: string | null;
	address?: string | null;
	orderDescription?: string | null;
	packageDescription?: string | null;
	orderPrice: number;
	transportPrice: number;
	displayId?: string | null;
	sellerId: number;
	statusId: number;
	internationalStatusId: number;
	statusDescription?: string | null;
	statusColorHexCode?: string | null;
	createdAt: string | Date;
	sellerUserName?: string | null;
	uniqueSellerIdentifier?: string | null;
};
const IncorrectInsertedOrders = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const countries: any = [
		{ value: 1, label: "Kosova" },
		{ value: 2, label: "Albania" },
		{ value: 3, label: "Macedonia" },
	];
	const hexToRgba = (hex: string, alpha: number) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	};

	let grid = useRef<AgGridReact | null>(null);
	const [tableData, setTableData] = useState<SelectIncorrectOrderDetails[]>(
		[]
	);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [selectedOrderIds, setSelectedOrderIds] = useState<any[]>([]);
	const [countryId, setCountryId] = useState<number | null>();
	const [countryDropDown, setCountryDropDown] = useState<any>();

	const fetchConfiguration = async () => {
		const countryConfigurationValue = await getConfigurationValue(2);
		const countryValue = Number(countryConfigurationValue);
		const filteredCountries = countries.filter(
			(country: any) => country.value !== countryValue
		);
		console.log(filteredCountries, "filteredCountries");
		setCountryDropDown(filteredCountries);
	};

	const selectIncorrectInsertedOrders = async (
		selectedCountry: number | null
	) => {
		try {
			const response = await api.get<SelectIncorrectOrderDetails[]>(
				"Order/select-incorect-orders",
				{ params: { countryId: selectedCountry } }
			);
			if (response.status === 200 && response.data.length > 0) {
				console.log(response, "response");
				setTableData(response.data);
			} else {
				setTableData([]);
			}
		} catch (error) {
			console.log(error);
			setTableData([]);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		fetchConfiguration();
		setTableColumns([
			{
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "displayId",
				headerName: "Kodi",
				valueFormatter: (params) => codeFormatter(params.value),
				suppressSizeToFit: isSmallScreen,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 140,
			},
			{
				field: "refid",
				headerName: "Referenca",
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusDescription",
				headerName: "Statusi",
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								{/* <p className="w-full font-medium"> */}
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: "Data e regjistrimit",
				valueFormatter: (params) => valueDateFormatter(params?.value),
				width: 140,
			},
			{
				headerName: "Shitesi",
				cellRendererFramework: (params: any) => {
					const sellerUserName = params.data.sellerUserName;
					const sellerUniqueId = params.data.uniqueSellerIdentifier;
					const sellerString: string =
						sellerUserName + " (" + sellerUniqueId + ") ";
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerString}
							</p>
						</div>
					);
				},

				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerNameAndSurname",
				headerName: "Bleresi",
				width: 160,
				cellRendererFramework: (params: any) => {
					const buyerNameSurname = params.data.buyerNameAndSurname;
					const phone = params.data.buyerPhoneNo;
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{buyerNameSurname}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{phone}
							</p>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "address",
				headerName: "Adresa",
				width: 180,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: "Emertimi",
				width: 180,
				suppressSizeToFit: isSmallScreen,
				hide: true,
			},
			{
				field: "packageDescription",
				headerName: "Pershkrimi",
			},
			{
				field: "country",
				headerName: "Shteti",
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: "Qyteti",
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				valueGetter: (params: any) => {
					return params.data.orderPrice
						? `${CONFIG?.CURRENCY} ${params.data.orderPrice}`
						: "- " + CONFIG?.CURRENCY;
				},
				width: 130,
				headerName: "Vlera e pakos ME TVSH",
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: "Vlera e transportit",
				valueGetter: (params: any) => {
					return params.data.transportPrice
						? `${CONFIG?.CURRENCY} ${params.data.transportPrice}`
						: "- " + CONFIG?.CURRENCY;
				},
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "register",
				headerName: " ",
				width: 130,
				cellRenderer: (params: any) => {
					return (
						<Button size="large" style={{ color: "green" }}>
							Regjistro
						</Button>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, []);

	const onSelectionChanged = () => {
		const selectedNodes = grid.current?.api.getSelectedNodes();
		const selectedOrders =
			selectedNodes?.map((node) => ({
				id: node.data.id,
			})) || [];
		setSelectedOrderIds(selectedOrders);
	};

	const onCountrySelect = async (e: any) => {
		if (e) {
			setCountryId(e);
			selectIncorrectInsertedOrders(e);
		} else {
			setCountryId(null);
			setTableData([]);
		}
	};

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "register":
				await registerOrder(params?.data);
				break;
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.displayId?.toString();
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const registerOrder = async (data: any) => {
		const orderId = data.displayId;
		try {
			const result = await api.post(
				"Order/insert-incorrect-order",
				null,
				{
					params: { orderId: orderId },
				}
			);
			if (result.status === 200) {
				selectIncorrectInsertedOrders(countryId ?? null);
				toast.success("Porosia u insertu me sukses!");
			}
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<>
			<div>
				<DefaultSelect
					label={"Selekto Shtetin"}
					flexDirection="col"
					alignCenter="start"
					placeholder={"Selekto"}
					className="registerInput"
					options={countryDropDown}
					value={countryId}
					onChange={onCountrySelect}
					allowClear
				/>
			</div>
			<div
				className="ag-theme-alpine"
				style={{
					width: "100%",
					height: "800px",
					// overflowX: "auto"
				}}
			>
				<AgGridReact
					rowData={tableData}
					columnDefs={tableColumns}
					defaultColDef={defaultColDef}
					undoRedoCellEditing={true}
					undoRedoCellEditingLimit={20}
					suppressCopyRowsToClipboard={true}
					defaultExcelExportParams={defaultExcelExportSettings}
					rowSelection="single"
					ref={grid}
					enableRangeSelection={true}
					cacheQuickFilter={true}
					// domLayout="autoHeight"
					onCellClicked={onCellClicked}
					suppressPaginationPanel={false}
					rowHeight={60}
					// onFirstDataRendered={onFirstDataRendered}
					// onSelectionChanged={onSelectionChanged}
				/>
			</div>
		</>
	);
};

export default IncorrectInsertedOrders;
